import _, {chain} from 'lodash';
import {createSelector} from 'reselect';

import {findCardForContactInNamespace} from '../../../helpers/models/contactHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {CalendarEventModel} from '../../../models/api/calendarEventModel';
import {ContactModel} from '../../../models/api/contactModel';
import {ParticipantResponseStatusesEnum, ParticipantTypesEnum} from '../../../models/api/participantModel';
import {RecipientModel} from '../../../models/api/recipientModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {getChannelsBySendAddress} from '../channels/channelsSelectors';
import {getCards, getContacts} from '../dataSelectors';

/*
 * Multiple visual participants.
 */

type GetCalendarEventSelector = AppParametricSelector<any, CalendarEventModel>;

/** Find the contacts for the specified message. */
function makeGetCalendarEventContacts(getCalendarEvent: GetCalendarEventSelector) {
  return createSelector([getCalendarEvent, getContacts], (calendarEvent, contacts) =>
    calendarEvent.participants.map((participant) => {
      const {contactUid} = participant;
      const contact = contacts[contactUid];
      if (!contact) {
        throw new Error(`A contact (${contactUid}) could not be found for this participant.`);
      }

      return contact;
    }),
  );
}

type GetCalendarEventContactsSelector = AppParametricSelector<any, Array<ContactModel>>;
type GetNamespaceSelector = AppParametricSelector<any, string>;

/** Find the fallback contacts for the specified contacts. */
function makeGetCalendarEventFallbackContacts(getCalendarEventContacts: GetCalendarEventContactsSelector) {
  return createSelector([getCalendarEventContacts, getContacts], (calendarEventContacts, contacts) =>
    calendarEventContacts.map((c) => (c.fallbackContactUid && contacts[c.fallbackContactUid]) || undefined),
  );
}

/** Find the cards for the contacts of the current message. */
function makeGetCalendarEventCards(
  getNamespace: GetNamespaceSelector,
  getCalendarEventContacts: GetCalendarEventContactsSelector,
) {
  return createSelector([getNamespace, getCalendarEventContacts, getCards], (namespace, contacts, cards) =>
    contacts.map((contact) => findCardForContactInNamespace(contact, cards, namespace)),
  );
}

/** Find the channels for the contacts of the current message. */
function makeGetCalendarEventChannels(getCalendarEventContacts: GetCalendarEventContactsSelector) {
  return createSelector([getCalendarEventContacts, getChannelsBySendAddress], (contacts, channels) =>
    contacts.map((contact) => {
      const {handle, source} = contact;
      const channel = channels[handle];
      return (channel && channel.contactSource === source && channel) || undefined;
    }),
  );
}

/** Build a flat list of visual participants for the specified calendar event. */
export function makeGetVisualParticipants(
  getNamespace: GetNamespaceSelector,
  getCalendarEvent: GetCalendarEventSelector,
) {
  const getCalendarEventContacts = makeGetCalendarEventContacts(getCalendarEvent);
  const getMessageFallbackContacts = makeGetCalendarEventFallbackContacts(getCalendarEventContacts);
  const getMessageCards = makeGetCalendarEventCards(getNamespace, getCalendarEventContacts);
  const getMessageChannels = makeGetCalendarEventChannels(getCalendarEventContacts);

  const getCalendarEventContactsShallow = wrapInShallowSelector(getCalendarEventContacts);
  const getMessageFallbackContactsShallow = wrapInShallowSelector(getMessageFallbackContacts);
  const getMessageCardsShallow = wrapInShallowSelector(getMessageCards);
  const getMessageChannelsShallow = wrapInShallowSelector(getMessageChannels);

  return createSelector(
    [
      getNamespace,
      getCalendarEvent,
      getCalendarEventContactsShallow,
      getMessageFallbackContactsShallow,
      getMessageCardsShallow,
      getMessageChannelsShallow,
    ],
    (namespace, calendarEvent, contacts, fallbackContacts, cards, channels) =>
      chain(calendarEvent.participants)
        .map((participant, index) => {
          // Find the corresponding resources.
          const contact = contacts[index];
          const fallbackContact = fallbackContacts[index];
          const card = cards[index];
          const channel = channels[index];

          // Build the Viewer Recipient.
          const result: VisualParticipantModel = {
            contact,
            namespace,
            fallbackContact,
            card,
            channel,
            name: participant.name,
            type: participant.type,
            isOrganizer: participant.isOrganizer,
            isCreator: participant.isCreator,
            isAttendee: participant.isAttendee,
            responseStatus: participant.responseStatus,
          };
          return result;
        })
        .orderBy([(p) => p.isOrganizer, (p) => (p.name || p.contact.handle).toLowerCase()], ['desc', 'asc'])
        .value(),
  );
}

/** Build a visual participant from the given recipient. */
export const getBuildVisualParticipantFromRecipient = createSelector(
  [getContacts],
  (contacts) => (recipient: RecipientModel) => {
    // Find the corresponding contact.
    const contact = _(contacts)
      .values()
      .compact()
      .find((c) => c.uid === recipient.contactUid);

    if (!contact) {
      return undefined;
    }

    const visualParticipant: VisualParticipantModel = {
      contact,
      name: recipient.name || undefined,
      namespace: 'global',
      type: ParticipantTypesEnum.REQUIRED,
      isOrganizer: false,
      isCreator: false,
      isAttendee: true,
      responseStatus: ParticipantResponseStatusesEnum.PENDING,
    };

    return visualParticipant;
  },
);
